<script setup>
import { computed, ref } from 'vue';
import { required, minLength, sameAs } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import AgentAPI from 'dashboard/api/agents';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['close']);
const { t } = useI18n();

const password = ref('');
const passwordConfirmation = ref('');
const isSaving = ref(false);

const rules = {
  password: { required, minLength: minLength(6) },
  passwordConfirmation: {
    required,
    sameAsPassword: sameAs(password),
  },
};

const v$ = useVuelidate(rules, { password, passwordConfirmation });
const title = computed(
  () => `${t('AGENT_MGMT.EDIT.PASSWORD_RESET.TITLE')} - ${props.name}`
);

const resetPassword = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  isSaving.value = true;
  try {
    await AgentAPI.resetPassword(
      props.id,
      password.value,
      passwordConfirmation.value
    );
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE');
    useAlert(message);
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex h-auto flex-col overflow-auto">
    <woot-modal-header
      :header-title="title"
      :header-content="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.DESCRIPTION')"
    />
    <form class="flex w-full flex-col" @submit.prevent="resetPassword">
      <label :class="{ error: v$.password.$error }">
        {{ $t('AGENT_MGMT.EDIT.PASSWORD_RESET.PASSWORD_LABEL') }}
        <input
          v-model="password"
          type="password"
          autocomplete="new-password"
          :placeholder="
            $t('AGENT_MGMT.EDIT.PASSWORD_RESET.PASSWORD_PLACEHOLDER')
          "
          @input="v$.password.$touch"
        />
        <span class="message">
          {{ $t('AGENT_MGMT.EDIT.PASSWORD_RESET.PASSWORD_HINT') }}
        </span>
      </label>

      <label :class="{ error: v$.passwordConfirmation.$error }">
        {{ $t('AGENT_MGMT.EDIT.PASSWORD_RESET.CONFIRMATION_LABEL') }}
        <input
          v-model="passwordConfirmation"
          type="password"
          autocomplete="new-password"
          :placeholder="
            $t('AGENT_MGMT.EDIT.PASSWORD_RESET.CONFIRMATION_PLACEHOLDER')
          "
          @input="v$.passwordConfirmation.$touch"
        />
        <span v-if="v$.passwordConfirmation.$error" class="message">
          {{ $t('AGENT_MGMT.EDIT.PASSWORD_RESET.CONFIRMATION_ERROR') }}
        </span>
      </label>

      <div class="flex w-full justify-end gap-2 py-2">
        <Button
          faded
          slate
          type="button"
          :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
          @click="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.SUBMIT')"
          :disabled="v$.$invalid || isSaving"
          :is-loading="isSaving"
        />
      </div>
    </form>
  </div>
</template>
